import { format } from 'date-fns'
import { useAdminSettings } from '@/hooks/useAdminSettings'
import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardDescription, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'

// Seeded with the March 2026 admin panel but never read by the app, the site or
// any edge function — their switches changed nothing, so they are not shown.
// The rows stay in admin.system_settings untouched.
const RETIRED_KEYS = new Set(['maintenance_mode', 'signup_enabled', 'share_moderation', 'default_unit'])

export function SystemSettingsPage() {
  const { data: settings, isLoading } = useAdminSettings()
  const rows = (settings ?? []).filter((s) => !RETIRED_KEYS.has(s.key))

  if (isLoading) {
    return (
      <div>
        <PageHeader title="System Settings" description="Stored application settings (read-only)" />
        <div className="space-y-4">
          {[1, 2].map((i) => <Skeleton key={i} className="h-24 w-full" />)}
        </div>
      </div>
    )
  }

  return (
    <div>
      <PageHeader title="System Settings" description="Stored application settings (read-only)" />

      {rows.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center text-sm text-muted-foreground">
            No settings stored.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {rows.map((s) => (
            <Card key={s.key}>
              <CardContent className="p-6">
                <div className="flex items-baseline justify-between gap-4">
                  <CardTitle className="font-mono text-base">{s.key}</CardTitle>
                  {s.updated_at && (
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      Updated {format(new Date(s.updated_at), 'MMM d, yyyy HH:mm')}
                    </span>
                  )}
                </div>
                {s.description && <CardDescription className="mt-1">{s.description}</CardDescription>}
                <pre className="mt-3 overflow-x-auto rounded-md bg-muted p-3 text-xs">
                  {JSON.stringify(s.value, null, 2)}
                </pre>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
